import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Scatter } from 'react-chartjs-2';
import { Chart as ChartJS, LinearScale, PointElement, LineElement, Title, Tooltip as ChartTooltip, Legend as ChartLegend } from 'chart.js';
import api from '../services/api';

// Register Chart.js components
ChartJS.register(LinearScale, PointElement, LineElement, Title, ChartTooltip, ChartLegend);

// Helper functions for size categorization
const getSizeCategory = (participants) => {
  if (!participants) return 'Unknown';
  if (participants <= 10) return 'Micro';
  if (participants <= 25) return 'Small';
  if (participants <= 50) return 'Medium-Small';
  if (participants <= 100) return 'Medium';
  if (participants <= 250) return 'Medium-Large';
  if (participants <= 500) return 'Large';
  if (participants <= 2000) return 'Extra-Large';
  return 'Mega';
};

const getSizeRange = (participants) => {
  if (!participants) return 'Unknown';
  if (participants <= 10) return '1-10 participants';
  if (participants <= 25) return '11-25 participants';
  if (participants <= 50) return '26-50 participants';
  if (participants <= 100) return '51-100 participants';
  if (participants <= 250) return '101-250 participants';
  if (participants <= 500) return '251-500 participants';
  if (participants <= 2000) return '501-2000 participants';
  return '2001+ participants';
};

// Currency formatting with commas
const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

// Short currency format for axis labels (no decimals)
const formatCurrencyShort = (value) => {
  if (value === null || value === undefined) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Custom donut/ring shape for target company
const renderDonut = (props) => {
  const { cx, cy, fill } = props;
  const outerRadius = 12; // Outer circle radius
  const innerRadius = 7;  // Inner circle radius (creates the "hole")
  
  return (
    <g>
      {/* Outer circle */}
      <circle
        cx={cx}
        cy={cy}
        r={outerRadius}
        fill={fill}
        stroke="#B91C1C"  // Darker red border
        strokeWidth="2"
      />
      {/* Inner circle (creates the donut hole) */}
      <circle
        cx={cx}
        cy={cy}
        r={innerRadius}
        fill="white"  // White hole in the middle
      />
    </g>
  );
};

// Chart.js Scatter Plot Component
const ScatterPlotChart = ({ scatterData, formatCurrencyShort }) => {
  const chartRef = useRef(null);
  
  if (!scatterData || !scatterData.peer_companies || scatterData.peer_companies.length === 0) {
    return (
      <div style={{ width: '100%', height: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
        No peer data available
      </div>
    );
  }

  // Prepare Chart.js data
  const peerDataset = {
    label: 'Peer Plans',
    data: (scatterData.peer_companies || []).map(peer => ({
      x: peer.participants_with_balance,
      y: peer.cost_per_participant,
      name: peer.name,
      ein: peer.ein,
      assets: peer.assets
    })),
    backgroundColor: 'rgba(107, 114, 128, 0.6)',
    borderColor: 'rgba(107, 114, 128, 0.8)',
    borderWidth: 1,
    pointRadius: 4,
    pointHoverRadius: 6,
    pointHoverBorderWidth: 2
  };

  const targetDataset = scatterData.target_company ? {
    label: 'Your Plan',
    data: [{
      x: scatterData.target_company.participants_with_balance,
      y: scatterData.target_company.cost_per_participant,
      name: scatterData.target_company.name,
      ein: scatterData.target_company.ein,
      assets: scatterData.target_company.assets,
      isTarget: true
    }],
    backgroundColor: 'rgba(220, 38, 38, 0.9)',
    borderColor: '#B91C1C',
    borderWidth: 3,
    pointRadius: 10,
    pointHoverRadius: 12,
    pointStyle: 'circle',
    pointBorderWidth: 3
  } : null;

  const datasets = [peerDataset];
  if (targetDataset) datasets.push(targetDataset);

  const chartData = { datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          padding: 15,
          font: { size: 12, weight: '600' },
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'white',
        titleColor: '#111827',
        bodyColor: '#374151',
        borderColor: '#e5e7eb',
        borderWidth: 2,
        padding: 12,
        displayColors: false,
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 13 },
        callbacks: {
          title: (items) => items[0].raw.name || 'Company',
          label: (context) => {
            const point = context.raw;
            const labels = [
              `Cost: ${formatCurrencyShort(point.y)}/participant`,
              `Participants: ${point.x.toLocaleString()}`
            ];
            if (point.assets > 0) {
              labels.push(`Assets: ${formatCurrencyShort(point.assets)}`);
            }
            return labels;
          },
          afterLabel: (context) => {
            const point = context.raw;
            return point.isTarget ? '\n★ YOUR PLAN' : '';
          }
        }
      }
    },
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        title: {
          display: true,
          text: 'Participants with Balance',
          font: { size: 12, weight: '600' },
          color: '#737373'
        },
        ticks: {
          color: '#737373',
          font: { size: 11 }
        },
        grid: {
          color: '#E5E5E5',
          drawTicks: true
        }
      },
      y: {
        type: 'linear',
        title: {
          display: true,
          text: 'Cost per Participant',
          font: { size: 12, weight: '600' },
          color: '#737373'
        },
        ticks: {
          color: '#737373',
          font: { size: 11 },
          callback: (value) => formatCurrencyShort(value)
        },
        grid: {
          color: '#E5E5E5',
          drawTicks: true
        }
      }
    },
    interaction: {
      mode: 'nearest',
      intersect: true
    }
  };

  return (
    <div style={{ width: '100%', height: '500px', position: 'relative' }}>
      <Scatter
        ref={chartRef}
        data={chartData}
        options={options}
      />
    </div>
  );
};


const ComparisonTab = ({ ein, planNumber }) => {
  const [comparisonData, setComparisonData] = useState([]);
  const [scatterData, setScatterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scatterLoading, setScatterLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scatterError, setScatterError] = useState(null);

  useEffect(() => {
    const fetchComparisonData = async () => {
      try {
        setLoading(true);
        const data = await api.getPeerComparison(ein, planNumber);
        
        // Validate that data is an array before setting state
        if (Array.isArray(data)) {
          setComparisonData(data);
        } else {
          console.error('API returned non-array data:', data);
          setComparisonData([]);
          setError('Invalid data format received from server');
        }
      } catch (err) {
        console.error('Error fetching comparison data:', err);
        setError(err.message);
        setComparisonData([]); // Ensure it's always an array
      } finally {
        setLoading(false);
      }
    };

    const fetchScatterData = async () => {
      try {
        setScatterLoading(true);
        const data = await api.getScatterPlotData(ein, planNumber);
        
        // DEBUG: Log original API response
        console.log('🔍 ORIGINAL API RESPONSE:');
        console.log('Target Company:', data.target_company);
        console.log('Peer Companies (first 3):', data.peer_companies?.slice(0, 3));
        
        // Transform API data to match chart expectations
        // API returns: participants, sponsor_name, ein, plan_number, cost_per_participant, is_target
        // Chart expects: participants_with_balance, name, cost_per_participant, assets
        if (data && typeof data === 'object') {
          // Filter out peers with negative costs
          const filteredPeers = data.peer_companies?.filter(peer => 
            peer.cost_per_participant >= 0
          ) || [];
          
          const transformedData = {
            ...data,
            peer_companies: filteredPeers.map(peer => ({
              ...peer,
              participants_with_balance: peer.participants,
              name: peer.sponsor_name,
              assets: 0 // API doesn't provide this, set to 0 for now
            })),
            target_company: data.target_company ? {
              ...data.target_company,
              participants_with_balance: data.target_company.participants,
              name: data.target_company.sponsor_name,
              assets: 0 // API doesn't provide this, set to 0 for now
            } : null
          };
          
          console.log('✅ TRANSFORMED DATA:');
          console.log('Target Company:', transformedData.target_company);
          console.log('Peer Companies (first 3):', transformedData.peer_companies?.slice(0, 3));
          
          setScatterData(transformedData);
        } else {
          console.error('API returned invalid scatter data:', data);
          setScatterData(null);
          setScatterError('Invalid scatter plot data format');
        }
      } catch (err) {
        console.error('Error fetching scatter data:', err);
        setScatterError(err.message);
        setScatterData(null);
      } finally {
        setScatterLoading(false);
      }
    };

    if (ein && planNumber) {
      fetchComparisonData();
      fetchScatterData();
    }
  }, [ein, planNumber]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading comparison data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  if (!comparisonData || comparisonData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">No comparison data available</div>
      </div>
    );
  }

  // Additional safety check to ensure it's an array
  if (!Array.isArray(comparisonData)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Invalid data format</div>
      </div>
    );
  }

  // Calculate statistics - data comes in DESC order, so first item is latest year
  const latestYear = comparisonData[0];  // Changed from comparisonData.length - 1
  const avgCompanyCost = comparisonData.reduce((sum, d) => sum + (d.company_cost_per_participant || 0), 0) / comparisonData.length;
  const avgPeerCost = comparisonData.reduce((sum, d) => sum + (d.peer_avg_cost_per_participant || 0), 0) / comparisonData.length;
  const costDifferencePercent = ((avgCompanyCost - avgPeerCost) / avgPeerCost * 100).toFixed(0);
  
  // Latest year statistics (for summary cards)
  const latestYearDiff = latestYear.company_cost_per_participant - latestYear.peer_avg_cost_per_participant;
  const latestYearDiffPercent = ((latestYearDiff / latestYear.peer_avg_cost_per_participant) * 100).toFixed(0);

  // Format data for chart - reverse to show oldest to newest (ascending order)
  const chartData = [...comparisonData].reverse().map(item => ({
    year: item.plan_year,
    'Your Plan': item.company_cost_per_participant,
    'Peer Average': item.peer_avg_cost_per_participant,
    'Peer Median': item.peer_median_cost_per_participant
  }));

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const yearData = comparisonData.find(d => d.plan_year === label);
      return (
        <div className="bg-white p-4 border-2 border-neutral-200 rounded-xl shadow-lg">
          <p className="font-semibold mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
          {yearData && yearData.peer_plan_count && (
            <p className="text-gray-500 text-xs mt-2">
              Based on {yearData.peer_plan_count} similar plans
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header with Statistics */}
      <div className="bg-gradient-to-r from-teal/10 to-teal/20 rounded-2xl p-8 border-2 border-teal/30 shadow-soft">
        <h2 className="text-2xl font-bold text-teal-dark mb-4">
          Cost Comparison vs Similar Plans
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
          <div className="bg-white rounded-xl p-4 border-2 border-neutral-200">
            <div className="text-sm text-gray-600 mb-1 font-medium uppercase tracking-wide">Latest Year</div>
            <div className="text-2xl font-bold text-teal-dark">
              {latestYear.plan_year}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Most recent data
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border-2 border-neutral-200">
            <div className="text-sm text-gray-600 mb-1 font-medium uppercase tracking-wide">Your Cost</div>
            <div className="text-2xl font-bold text-teal-dark">
              {formatCurrency(latestYear.company_cost_per_participant)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              per participant
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border-2 border-neutral-200">
            <div className="text-sm text-gray-600 mb-1 font-medium uppercase tracking-wide">Peer Average</div>
            <div className="text-2xl font-bold text-gray-700">
              {formatCurrency(latestYear.peer_avg_cost_per_participant)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {latestYear.peer_plan_count} similar plans
            </div>
          </div>
          
          <div className={`bg-white rounded-xl p-4 border-2 ${
            latestYear.company_cost_per_participant > latestYear.peer_avg_cost_per_participant ? 'border-coral/40 bg-red-50/30' : 'border-green-200 bg-green-50/30'
          }`}>
            <div className="text-sm text-gray-600 mb-1 font-medium uppercase tracking-wide">Difference</div>
            <div className={`text-2xl font-bold ${
              latestYear.company_cost_per_participant > latestYear.peer_avg_cost_per_participant ? 'text-coral' : 'text-green-600'
            }`}>
              {latestYearDiffPercent}%
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {latestYear.company_cost_per_participant > latestYear.peer_avg_cost_per_participant ? 'above' : 'below'} average
            </div>
          </div>
        </div>
      </div>

      {/* Line Chart: Historical Trend */}
      <div className="bg-white rounded-2xl shadow-soft p-8 border-2 border-neutral-100">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">
          Cost Per Participant Over Time
        </h3>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
            <XAxis 
              dataKey="year" 
              tick={{ fill: '#737373' }}
            />
            <YAxis 
              tickFormatter={formatCurrencyShort}
              tick={{ fill: '#737373' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="Your Plan" 
              stroke="#12A493" 
              strokeWidth={4} 
              dot={{ r: 5, fill: '#12A493' }}
              activeDot={{ r: 7 }}
            />
            <Line 
              type="monotone" 
              dataKey="Peer Average" 
              stroke="#9CA3AF" 
              strokeWidth={3}
              strokeDasharray="5 5"
              dot={{ r: 4 }}
            />
            <Line 
              type="monotone" 
              dataKey="Peer Median" 
              stroke="#D1D5DB" 
              strokeWidth={2}
              strokeDasharray="3 3"
              dot={{ r: 3 }}
            />
          </LineChart>
        </ResponsiveContainer>
        <p className="text-sm text-gray-600 mt-6 leading-relaxed">
          This chart shows how your plan's administrative costs compare to similar plans over time.
          The teal line represents your plan, while gray lines show peer benchmarks.
        </p>
      </div>

      {/* Scatter Plot: Peer Distribution */}
      {!scatterLoading && !scatterError && scatterData && (
        <div className="bg-white rounded-2xl shadow-soft p-8 border-2 border-neutral-100">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">
            Cost Distribution vs Peer Group ({scatterData.target_year || 'Latest'})
          </h3>
          
          {scatterData.peer_companies && scatterData.peer_companies.length > 0 ? (
            <>
              {(() => {
                // Calculate X-axis domain dynamically
                const allData = [
                  ...scatterData.peer_companies,
                  ...(scatterData.target_company ? [scatterData.target_company] : [])
                ];
                
                const participantValues = allData
                  .map(d => d.participants_with_balance)
                  .filter(v => v != null && v >= 0);
                
                const minParticipants = Math.min(...participantValues);
                const maxParticipants = Math.max(...participantValues);
                
                // Only use dynamic domain if min is far from 0 (e.g., > 20)
                // Add 10% padding on both sides for better visualization
                const useDynamicDomain = minParticipants > 20;
                const padding = (maxParticipants - minParticipants) * 0.1;
                const xDomain = useDynamicDomain 
                  ? [Math.max(0, minParticipants - padding), maxParticipants + padding]
                  : [0, maxParticipants + padding];
                
                return (
                  <ScatterPlotChart scatterData={scatterData} formatCurrencyShort={formatCurrencyShort} />
                )})()}

              {/* Statistics below the chart */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-teal/5 rounded-xl p-4 border-2 border-teal/20">
                  <div className="text-sm text-gray-600 mb-1 font-medium uppercase tracking-wide">Peer Group Size</div>
                  <div className="text-xl font-bold text-teal-dark">
                    {scatterData.total_peers} plans
                  </div>
                  <div className="text-xs text-gray-500">
                    showing {scatterData.peer_companies.length}
                  </div>
                </div>
                
                <div className="bg-green-50 rounded-xl p-4 border-2 border-green-200">
                  <div className="text-sm text-gray-600 mb-1 font-medium uppercase tracking-wide">Your Position</div>
                  <div className="text-xl font-bold text-green-600">
                    {scatterData.target_company && formatCurrency(scatterData.target_company.cost_per_participant)}
                  </div>
                  <div className="text-xs text-gray-500">
                    per participant
                  </div>
                </div>
                
                <div className="bg-purple-50 rounded-xl p-4 border-2 border-purple-200">
                  <div className="text-sm text-gray-600 mb-1 font-medium uppercase tracking-wide">Peer Average</div>
                  <div className="text-xl font-bold text-purple-600">
                    {formatCurrency(scatterData.average_cost)}
                  </div>
                  <div className="text-xs text-gray-500">
                    mean cost
                  </div>
                </div>
                
                <div className="bg-orange-50 rounded-xl p-4 border-2 border-orange-200">
                  <div className="text-sm text-gray-600 mb-1 font-medium uppercase tracking-wide">95th Percentile</div>
                  <div className="text-xl font-bold text-orange-600">
                    {formatCurrency(scatterData.percentile_95)}
                  </div>
                  <div className="text-xs text-gray-500">
                    chart maximum
                  </div>
                </div>
              </div>
              
              {/* Explanation note */}
              {scatterData.outliers_excluded > 0 && (
                <div className="mt-4 p-4 bg-neutral-50 rounded-xl border-2 border-neutral-200">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    <span className="font-semibold">📊 Chart Note:</span> To improve readability, 
                    {scatterData.outliers_excluded} peer plan{scatterData.outliers_excluded > 1 ? 's' : ''} with 
                    unusually high costs (above {formatCurrency(scatterData.percentile_95)}) 
                    {scatterData.outliers_excluded > 1 ? 'have' : 'has'} been excluded from the visualization. 
                    These represent{scatterData.outliers_excluded === 1 ? 's' : ''} the top 5% of costs in your peer group.
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg font-semibold mb-2">No Peer Data Available</p>
              <p className="text-sm">Unable to generate peer comparison for this plan size and industry.</p>
            </div>
          )}
        </div>
      )}

      {scatterLoading && (
        <div className="bg-white rounded-2xl shadow-soft p-6 border-2 border-neutral-100">
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Loading scatter plot data...</div>
          </div>
        </div>
      )}

      {scatterError && (
        <div className="bg-white rounded-2xl shadow-soft p-6 border-2 border-neutral-100">
          <div className="flex items-center justify-center h-64">
            <div className="text-red-500">Error loading scatter plot: {scatterError}</div>
          </div>
        </div>
      )}

      {/* Insights Section */}
      <div className="bg-white rounded-2xl shadow-soft p-6 border-2 border-neutral-100">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Key Insights</h3>
        
        <div className="space-y-3">
          {latestYear.company_cost_per_participant > latestYear.peer_avg_cost_per_participant * 1.5 && (
            <div className="flex items-start space-x-3 p-4 bg-red-50 rounded-xl border-2 border-red-200">
              <span className="text-coral text-2xl">⚠️</span>
              <div>
                <p className="font-semibold text-red-800">Significantly Higher Costs</p>
                <p className="text-sm text-red-700 leading-relaxed">
                  Your plan costs are {latestYearDiffPercent}% higher than similar plans. 
                  Consider reviewing your service provider fees and plan structure.
                </p>
              </div>
            </div>
          )}
          
          {latestYear.company_cost_per_participant > latestYear.peer_avg_cost_per_participant && latestYear.company_cost_per_participant <= latestYear.peer_avg_cost_per_participant * 1.5 && (
            <div className="flex items-start space-x-3 p-4 bg-yellow-50 rounded-xl border-2 border-yellow-200">
              <span className="text-yellow-600 text-2xl">⚡</span>
              <div>
                <p className="font-semibold text-yellow-800">Moderately Higher Costs</p>
                <p className="text-sm text-yellow-700 leading-relaxed">
                  Your plan costs are {latestYearDiffPercent}% higher than similar plans. 
                  There may be opportunities for cost savings.
                </p>
              </div>
            </div>
          )}
          
          {latestYear.company_cost_per_participant <= latestYear.peer_avg_cost_per_participant && (
            <div className="flex items-start space-x-3 p-4 bg-green-50 rounded-xl border-2 border-green-200">
              <span className="text-green-600 text-2xl">✓</span>
              <div>
                <p className="font-semibold text-green-800">Competitive Costs</p>
                <p className="text-sm text-green-700 leading-relaxed">
                  Your plan costs are competitive with similar plans. Great job!
                </p>
              </div>
            </div>
          )}

          <div className="flex items-start space-x-3 p-4 bg-teal/5 rounded-xl border-2 border-teal/20">
            <span className="text-teal text-2xl">ℹ️</span>
            <div>
              <p className="font-semibold text-teal-dark">About This Comparison</p>
              <p className="text-sm text-teal-dark leading-relaxed">
                This comparison includes {getSizeCategory(latestYear.company_participants)} plans ({getSizeRange(latestYear.company_participants)}) in your industry. 
                Costs shown are administrative expenses per participant with an account balance.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Data Table */}
      <div className="bg-white rounded-2xl shadow-soft p-6 border-2 border-neutral-100">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Year-by-Year Breakdown</h3>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Year</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">Your Cost</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">Peer Average</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">Difference</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">Similar Plans</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {comparisonData.slice().reverse().map((row) => {
                const diff = row.company_cost_per_participant - row.peer_avg_cost_per_participant;
                const diffPercent = (diff / row.peer_avg_cost_per_participant * 100).toFixed(0);
                return (
                  <tr key={row.plan_year} className="hover:bg-neutral-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {row.plan_year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-teal-dark font-semibold">
                      {formatCurrency(row.company_cost_per_participant)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600">
                      {formatCurrency(row.peer_avg_cost_per_participant)}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-semibold ${
                      diff > 0 ? 'text-coral' : 'text-green-600'
                    }`}>
                      {diff > 0 ? '+' : ''}{formatCurrency(diff)} ({diffPercent}%)
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">
                      {row.peer_plan_count?.toLocaleString('en-US') || 'N/A'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ComparisonTab;
